import { Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import Marquee from "@/components/site/Marquee";
import QuoteButton from "@/components/site/QuoteButton";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { WINDOWS, IMAGES } from "@/lib/site";

const PERFORM = [
  "Energy efficient — warmer for longer, lower bills",
  "Enhanced security with multi-point locking",
  "Reduced noise for a quieter, calmer home",
  "Low maintenance — a simple wipe keeps them new",
  "Stylish finishes to suit your home's character",
];

export default function Windows() {
  return (
    <>
      <Seo
        title="Windows | Bay & Standard Windows | Eminence Windows & Doors"
        description="Explore our window range — beautiful bay windows and standard windows with trickle vents, energy efficiency, security and low maintenance across Greater Manchester."
      />

      {/* Hero */}
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-charcoal" data-testid="windows-hero">
        <img
          src={IMAGES.windowInterior}
          alt="Bright living room with large windows"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-10">
          <p className="kicker text-gold">Our window range</p>
          <h1 className="mt-6 font-serif text-5xl leading-[1] text-cream md:text-7xl">
            <MaskReveal lines={["Beautifully designed,", "inside and out."]} />
          </h1>
          <p className="mt-6 max-w-xl text-cream/80">
            Timeless design with outstanding performance — comfort, security and
            energy efficiency all year round.
          </p>
        </div>
      </section>

      <Marquee
        items={["Bay Windows", "Casement", "Tilt & Turn", "Sliding", "CERTASS Certified Installers", "Range of Colours", "A+ Energy Rated"]}
      />

      {/* Products */}
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        {WINDOWS.map((w, i) => (
          <section
            key={w.slug}
            className="grid items-center gap-10 border-b border-gold/15 py-20 lg:grid-cols-2 lg:gap-16 md:py-28"
            data-testid={`window-${w.slug}`}
          >
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={w.image}
                  alt={w.name}
                  className="aspect-[5/4] w-full object-cover transition-transform duration-[900ms] hover:scale-105"
                />
              </div>
            </Reveal>

            <Reveal delay={0.1} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <p className="kicker text-gold-deep">{w.tagline}</p>
              <h2 className="mt-4 font-serif text-4xl text-ink md:text-5xl">
                {w.name}
              </h2>
              <p className="mt-5 leading-relaxed text-stone">{w.overview}</p>

              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {w.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ink">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-deep" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-9">
                <QuoteButton
                  label={`Get a quote for ${w.name}`}
                  testId={`window-${w.slug}-quote-btn`}
                />
              </div>
            </Reveal>
          </section>
        ))}
      </div>

      {/* Built to perform */}
      <section className="bg-charcoal py-24 text-cream md:py-32" data-testid="windows-perform">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <p className="kicker text-gold">Built for comfort, designed to perform</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
              Everything a modern window should be.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-x-12 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            {PERFORM.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="flex items-start gap-4 border-t border-white/10 pt-6">
                  <span className="font-serif text-2xl text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-cream/85">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14">
              <QuoteButton variant="gold" size="lg" testId="windows-request-quote-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
